<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Community-Based Health Insurance</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Keep your finances and membership up to date" name="description" />
    <meta content="Kelvin Njuguna Ndungu" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">

    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />

    <!-- Bootstrap select styling -->
    <link rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">

    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">

    <!-- ========== Left Sidebar Start ========== -->
    <NavigationBar/>
    <!-- Left Sidebar End -->

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="main-content">

      <div class="page-content">
        <div class="container-fluid">

          <!-- Check if user has any report permissions -->
          <div v-if="!hasAnyReportPermission">
            <!-- No Permissions Error Message -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body text-center py-5">
                    <i class="bx bx-lock-alt display-1 text-danger mb-4"></i>
                    <h3 class="text-danger">Access Denied</h3>
                    <p class="text-muted mb-4">
                      You do not have permission to view any premium collection reports.
                    </p>
                    <p class="text-muted">
                      Please contact your system administrator to request access to:
                    </p>
                    <ul class="list-unstyled text-muted">
                      <li><i class="bx bx-x text-danger"></i> Reports Hospitals</li>
                      <li><i class="bx bx-x text-danger"></i> Reports Agents</li>
                      <li><i class="bx bx-x text-danger"></i> Reports Member Deposits</li>
                      <li><i class="bx bx-x text-danger"></i> Reports Woreda Collections</li>
                      <li><i class="bx bx-x text-danger"></i> Reports Channels</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Show content only if user has at least one permission -->
          <div v-else>
            <!-- start page title -->
            <div class="row ">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Premium Collections Report - <span
                      class="active-date">{{ formatDateRangeDisplay(startDate, endDate) }}</span></h4>

                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript: void(0);">Premium Management</a></li>
                      <li class="breadcrumb-item"><a href="javascript: void(0);">Collections Report</a></li>
                      <li class="breadcrumb-item"><a href="javascript: void(0);">Summary</a></li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ethiopian/Gregorian Date Filter Section -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card mb-0">
                  <div class="card-body py-2">
                    <!-- Row 1: Calendar Toggle + Date Selection -->
                    <div class="row g-2 align-items-center">
                      <!-- Calendar Type Toggle -->
                      <div class="col-auto">
                        <div class="btn-group btn-group-sm" role="group">
                          <input type="radio" class="btn-check" name="calendarType" id="calEth" :value="true" v-model="showEthiopianDates">
                          <label class="btn btn-outline-primary" for="calEth">
                            <i class="bx bx-calendar me-1"></i>Ethiopian
                          </label>
                          <input type="radio" class="btn-check" name="calendarType" id="calGreg" :value="false" v-model="showEthiopianDates">
                          <label class="btn btn-outline-success" for="calGreg">
                            <i class="bx bx-calendar-alt me-1"></i>Gregorian
                          </label>
                        </div>
                      </div>

                      <!-- Date Selection -->
                      <div class="col">
                        <div class="row g-2 align-items-center">
                          <!-- From Date -->
                          <div class="col-auto">
                            <span class="badge bg-secondary">From</span>
                          </div>
                          <div class="col-auto">
                            <select v-model="dateFilterRange.fromDay" @change="updateDateRange" class="form-select form-select-sm" style="width: 65px;">
                              <option value="">{{ showEthiopianDates ? 'ቀን' : 'Day' }}</option>
                              <option v-for="d in fromDaysInMonth" :key="d" :value="d">{{ d }}</option>
                            </select>
                          </div>
                          <div class="col-auto">
                            <select v-model="dateFilterRange.fromMonth" @change="onFromMonthChange" class="form-select form-select-sm" style="width: 115px;">
                              <option value="">{{ showEthiopianDates ? 'ወር' : 'Month' }}</option>
                              <option v-for="m in displayMonths" :key="m.value" :value="m.value">
                                {{ showEthiopianDates ? m.label : m.labelShort }}
                              </option>
                            </select>
                          </div>
                          <div class="col-auto">
                            <select v-model="dateFilterRange.fromYear" @change="updateDateRange" class="form-select form-select-sm" style="width: 80px;">
                              <option value="">{{ showEthiopianDates ? 'ዓ/ም' : 'Year' }}</option>
                              <option v-for="y in displayYearOptions" :key="y" :value="y">{{ y }}</option>
                            </select>
                          </div>

                          <!-- Separator -->
                          <div class="col-auto">
                            <span class="text-muted">—</span>
                          </div>

                          <!-- To Date -->
                          <div class="col-auto">
                            <span class="badge bg-secondary">To</span>
                          </div>
                          <div class="col-auto">
                            <select v-model="dateFilterRange.toDay" @change="updateDateRange" class="form-select form-select-sm" style="width: 65px;">
                              <option value="">{{ showEthiopianDates ? 'ቀን' : 'Day' }}</option>
                              <option v-for="d in toDaysInMonth" :key="d" :value="d">{{ d }}</option>
                            </select>
                          </div>
                          <div class="col-auto">
                            <select v-model="dateFilterRange.toMonth" @change="onToMonthChange" class="form-select form-select-sm" style="width: 115px;">
                              <option value="">{{ showEthiopianDates ? 'ወር' : 'Month' }}</option>
                              <option v-for="m in displayMonths" :key="m.value" :value="m.value">
                                {{ showEthiopianDates ? m.label : m.labelShort }}
                              </option>
                            </select>
                          </div>
                          <div class="col-auto">
                            <select v-model="dateFilterRange.toYear" @change="updateDateRange" class="form-select form-select-sm" style="width: 80px;">
                              <option value="">{{ showEthiopianDates ? 'ዓ/ም' : 'Year' }}</option>
                              <option v-for="y in displayYearOptions" :key="y" :value="y">{{ y }}</option>
                            </select>
                          </div>

                          <!-- Action Buttons -->
                          <div class="col-auto">
                            <button type="button" class="btn btn-primary btn-sm" @click="applyDateFilter" :disabled="!isValidDateRange" title="Apply Filter">
                              <i class="bx bx-filter-alt"></i>
                            </button>
                          </div>
                          <div class="col-auto" v-if="hasDateSelection">
                            <button type="button" class="btn btn-outline-danger btn-sm" @click="clearDateFilter" title="Clear Filter">
                              <i class="bx bx-x"></i>
                            </button>
                          </div>

                          <!-- Quick Date Presets -->
                          <div class="col-auto ms-2">
                            <div class="btn-group btn-group-sm" role="group">
                              <button type="button" class="btn btn-outline-secondary" @click="setQuickDateRange('today')" title="Today">Today</button>
                              <button type="button" class="btn btn-outline-secondary" @click="setQuickDateRange('week')" title="This Week">Week</button>
                              <button type="button" class="btn btn-outline-secondary" @click="setQuickDateRange('month')" title="This Month">Month</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Row 2: Alternate Calendar Display -->
                    <div class="row mt-2" v-if="alternateDateDisplay">
                      <div class="col-12">
                        <small :class="showEthiopianDates ? 'text-success' : 'text-primary'">
                          <i class="bx bx-calendar-check me-1"></i>
                          <strong>{{ showEthiopianDates ? 'Gregorian' : 'Ethiopian' }}:</strong>
                          {{ alternateDateDisplay }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Member Statistics Dashboard -->
            <div class="row g-4">
              <!-- Total Collections -->
              <div class="col-lg-3 col-md-6">
                <div class="card stats-card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="stat-label">Total Collections</p>
                        <h3 class="stat-value">{{ formatCurrency(dashboardStats.totalCollections) }} <span class="currency">ETB</span></h3>
                        <div class="stat-change" :class="dashboardStats.collectionChange >= 0 ? 'positive' : 'negative'">
                          <i :class="['bx', 'me-1', dashboardStats.collectionChange >= 0 ? 'bx-trending-up' : 'bx-trending-down']"></i>
                          {{ dashboardStats.collectionChange >= 0 ? '+' : '' }}{{ dashboardStats.collectionChange }}% vs previous period
                        </div>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-primary rounded-circle text-white font-size-20">
                          <i class="bx bx-money"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Settlement Rate -->
              <div class="col-lg-3 col-md-6">
                <div class="card stats-card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="stat-label">Settlement Rate</p>
                        <h3 class="stat-value">{{ dashboardStats.settlementRate }}<span class="currency">%</span></h3>
                        <div class="stat-change positive">
                          <i class="bx bx-check-circle me-1"></i>{{ formatCurrency(dashboardStats.settledAmount) }} ETB settled
                        </div>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-success rounded-circle text-white font-size-20">
                          <i class="bx bx-transfer"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Member Deposits -->
              <div class="col-lg-3 col-md-6">
                <div class="card stats-card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="stat-label">Member Deposits</p>
                        <h3 class="stat-value">{{ formatNumber(dashboardStats.memberDeposits) }}</h3>
                        <div class="stat-change positive">
                          <i class="bx bx-user-plus me-1"></i>+{{ dashboardStats.newDeposits }} new deposits today
                        </div>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-purple rounded-circle text-white font-size-20">
                          <i class="bx bx-user"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-white p-0">
                    <!-- Nav tabs -->
                    <ul class="nav nav-tabs custom-nav-tabs align-items-center text-uppercase" role="tablist">

                      <li v-if="getRight('REPORTS HOSPITALS')" class="nav-item" role="presentation">
                        <a class="nav-link" :class="{'active': activeTab === 'hospitals'}" data-bs-toggle="tab" href="#overview" role="tab"
                           :aria-selected="activeTab === 'hospitals'" title="View collections made by hospitals" @click="switchTab('hospitals')">
                          <span class="d-block d-sm-none"><i class="fas fa-hospital"></i></span>
                          <span class="d-none d-sm-block">Hospitals</span>
                        </a>
                      </li>
                      <li v-if="getRight('REPORTS AGENTS')" class="nav-item" role="presentation">
                        <a class="nav-link" :class="{'active': activeTab === 'agents'}" data-bs-toggle="tab" href="#dependants" role="tab"
                           :aria-selected="activeTab === 'agents'" title="View collections made by agents or extension workers" @click="switchTab('agents')">
                          <span class="d-block d-sm-none"><i class="fas fa-user-tie"></i></span>
                          <span class="d-none d-sm-block">Agents</span>
                        </a>
                      </li>
                      <li v-if="getRight('REPORTS MEMBER DEPOSITS')" class="nav-item" role="presentation">
                        <a class="nav-link" :class="{'active': activeTab === 'memberDeposits'}" data-bs-toggle="tab" href="#activity" role="tab"
                           :aria-selected="activeTab === 'memberDeposits'" title="View self-service payments made by members" @click="switchTab('memberDeposits')">
                          <span class="d-block d-sm-none"><i class="fas fa-mobile-alt"></i></span>
                          <span class="d-none d-sm-block">Member Deposits</span>
                        </a>
                      </li>
                      <li v-if="getRight('REPORTS WOREDA COLLECTIONS')" class="nav-item" role="presentation">
                        <a class="nav-link" :class="{'active': activeTab === 'woredaCollections'}" data-bs-toggle="tab" href="#woreda" role="tab"
                           :aria-selected="activeTab === 'woredaCollections'" title="View woreda collections" @click="switchTab('woredaCollections')">
                          <span class="d-block d-sm-none"><i class="fas fa-mobile-alt"></i></span>
                          <span class="d-none d-sm-block">Woreda Collections</span>
                        </a>
                      </li>
                      <li v-if="getRight('REPORTS CHANNELS')" class="nav-item" role="presentation">
                        <a class="nav-link" :class="{'active': activeTab === 'channels'}" data-bs-toggle="tab" href="#channels" role="tab"
                           :aria-selected="activeTab === 'channels'" title="View channel reports" @click="switchTab('channels')">
                          <span class="d-block d-sm-none"><i class="fas fa-chart-bar"></i></span>
                          <span class="d-none d-sm-block">Channels Report</span>
                        </a>
                      </li>
                    </ul>
                  </div>

                  <div class="tab-content text-muted">
                    <!-- Hospitals Tab -->
                    <div class="tab-pane" :class="{'active show': activeTab === 'hospitals'}" id="overview" role="tabpanel">
                      <div class="card-header bg-white border-bottom">
                        <!-- Filter Bar -->
                        <div class="row g-2 mb-3">
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.regionId" @change="onRegionChange">
                              <option :value="null">All Regions</option>
                              <option v-for="region in regions" :key="region.region_id" :value="region.region_id">
                                {{ region.region_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.zoneId" @change="onZoneChange" :disabled="!filters.regionId">
                              <option :value="null">All Zones</option>
                              <option v-for="zone in zones" :key="zone.zone_id" :value="zone.zone_id">
                                {{ zone.zone_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.woredaId" @change="onWoredaChange" :disabled="!filters.zoneId">
                              <option :value="null">All Woredas</option>
                              <option v-for="woreda in woredas" :key="woreda.woreda_id" :value="woreda.woreda_id">
                                {{ woreda.woreda_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.hospitalId" @change="onHospitalChange">
                              <option :value="null">All Facilities</option>
                              <option v-for="hospital in hospitals" :key="hospital.hospital_id" :value="hospital.hospital_id">
                                {{ hospital.hospital_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-1 d-flex align-items-center">
                            <button v-if="hasActiveFilters" type="button" class="btn btn-sm btn-link text-danger p-0" @click="clearAllFilters" title="Clear all filters">
                              <i class="bx bx-x-circle"></i> Clear
                            </button>
                          </div>
                        </div>

                        <!-- Search and Download Bar -->
                        <div class="d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center gap-3">
                            <div class="d-flex align-items-center">
                              <label class="me-2 text-nowrap small">Show</label>
                              <select class="form-select form-select-sm" style="width: 80px;" v-model="perPage" @change="onPerPageChange">
                                <option :value="10">10</option>
                                <option :value="25">25</option>
                                <option :value="50">50</option>
                                <option :value="100">100</option>
                              </select>
                              <label class="ms-2 text-nowrap small">entries</label>
                            </div>
                            <div class="search-box mb-0">
                              <div class="position-relative">
                                <input type="text" id="searchInput" v-model="searchTerm"
                                       class="form-control bg-light border-light rounded"
                                       placeholder="Search hospitals, regions, woredas..." @input="handleSearch">
                                <i class="bx bx-search-alt search-icon"></i>
                              </div>
                            </div>
                            <button type="button" class="btn btn-sm btn-outline-secondary" @click="refreshData" :disabled="loading">
                              <i class="bx bx-refresh" :class="{'bx-spin': loading}"></i>
                            </button>
                          </div>
                          <div>
                            <button type="button" class="btn btn-primary text-capitalize" @click="downloadReport">
                              <i class="bx bxs-download"></i>
                              <span class="ms-1 d-none d-md-inline">Download Report</span>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="card-body">
                        <div>
                          <h4 class="card-title text-uppercase">
                            Premium Collections by Hospitals
                            <span class="text-primary active-date">{{ formatDateRangeDisplay(startDate, endDate) }}</span>
                          </h4>
                          <p>{{ getFilterDescription() }}</p>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loading" class="text-center py-5">
                          <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                          </div>
                          <p class="mt-2">Loading hospital data...</p>
                        </div>

                        <!-- Error State -->
                        <div v-else-if="error" class="alert alert-danger" role="alert">
                          <i class="bx bx-error-circle me-2"></i>{{ error }}
                        </div>

                        <!-- Data Table -->
                        <div v-else class="table-responsive">
                          <table class="table align-middle table-hover border table-striped text-nowrap mb-0">
                            <thead class="table-light">
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">Facility Name</th>
                              <th scope="col">Region</th>
                              <th scope="col">Woreda</th>
                              <th scope="col">Facility Type</th>
                              <th scope="col" class="text-center">Transactions</th>
                              <th scope="col" class="text-right">Total Collection</th>
                              <th scope="col" class="text-center">Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(hospital, index) in hospitalCollections" :key="hospital.hospital_id">
                              <td>{{ getRowNumber(index) }}</td>
                              <td>{{ hospital.hospital_name }}</td>
                              <td>{{ hospital.region_name }}</td>
                              <td>{{ hospital.woreda_name }}</td>
                              <td>{{ hospital.category_name }}</td>
                              <td class="text-center">{{ hospital.transaction_count }}</td>
                              <td class="text-right">{{ formatCurrency(hospital.total_collection) }} ETB</td>
                              <td class="text-center">
                                <span :class="['badge', 'rounded-pill', 'fw-bold', hospital.settled ? 'bg-success' : 'bg-danger']">
                                  {{ hospital.settled ? 'Yes' : 'No' }}
                                </span>
                              </td>
                            </tr>
                            <tr v-if="hospitalCollections.length === 0">
                              <td colspan="8" class="text-center py-4 text-muted">
                                <i class="bx bx-info-circle display-6"></i>
                                <p class="mt-2">No hospital collections found for the selected criteria.</p>
                              </td>
                            </tr>
                            </tbody>
                            <tfoot class="table-light text-uppercase" v-if="hospitalSummary">
                            <tr>
                              <th colspan="5">Total Hospitals: {{ hospitalSummary.total_hospitals }}</th>
                              <th class="text-center">{{ hospitalSummary.total_transactions }}</th>
                              <th class="text-right">{{ formatCurrency(hospitalSummary.total_amount) }} ETB</th>
                              <th class="text-center">
                                <span class="badge rounded-pill bg-info fw-bold">
                                  {{ hospitalSummary.settled_percentage }}% Settled
                                </span>
                              </th>
                            </tr>
                            </tfoot>
                          </table>

                          <!-- Pagination -->
                          <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.total > 0">
                            <div class="text-muted small">
                              Showing {{ showingFrom }} to {{ showingTo }} of {{ pagination.total }} entries
                            </div>
                            <nav aria-label="Page navigation">
                              <ul class="pagination pagination-sm mb-0">
                                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(1)" title="First">
                                    <i class="bx bx-chevrons-left"></i>
                                  </a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                  <a class="page-link" href="#" @click.prevent="previousPage" title="Previous">
                                    <i class="bx bx-chevron-left"></i>
                                  </a>
                                </li>
                                <li class="page-item" v-for="page in visiblePages" :key="page"
                                    :class="{ active: page === pagination.current_page }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                                  <a class="page-link" href="#" @click.prevent="nextPage" title="Next">
                                    <i class="bx bx-chevron-right"></i>
                                  </a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(pagination.total_pages)" title="Last">
                                    <i class="bx bx-chevrons-right"></i>
                                  </a>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Agents Tab -->
                    <div class="tab-pane" :class="{'active show': activeTab === 'agents'}" id="dependants" role="tabpanel">
                      <div class="card-header bg-white border-bottom">
                        <!-- Filter Bar -->
                        <div class="row g-2 mb-3">
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.regionId" @change="onRegionChange">
                              <option :value="null">All Regions</option>
                              <option v-for="region in regions" :key="region.region_id" :value="region.region_id">
                                {{ region.region_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.zoneId" @change="onZoneChange" :disabled="!filters.regionId">
                              <option :value="null">All Zones</option>
                              <option v-for="zone in zones" :key="zone.zone_id" :value="zone.zone_id">
                                {{ zone.zone_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.woredaId" @change="onWoredaChange" :disabled="!filters.zoneId">
                              <option :value="null">All Woredas</option>
                              <option v-for="woreda in woredas" :key="woreda.woreda_id" :value="woreda.woreda_id">
                                {{ woreda.woreda_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.hospitalId" @change="onHospitalChange">
                              <option :value="null">All Facilities</option>
                              <option v-for="hospital in hospitals" :key="hospital.hospital_id" :value="hospital.hospital_id">
                                {{ hospital.hospital_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-1 d-flex align-items-center">
                            <button v-if="hasActiveFilters" type="button" class="btn btn-sm btn-link text-danger p-0" @click="clearAllFilters" title="Clear all filters">
                              <i class="bx bx-x-circle"></i> Clear
                            </button>
                          </div>
                        </div>

                        <!-- Search and Download Bar -->
                        <div class="d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center gap-3">
                            <div class="d-flex align-items-center">
                              <label class="me-2 text-nowrap small">Show</label>
                              <select class="form-select form-select-sm" style="width: 80px;" v-model="perPage" @change="onPerPageChange">
                                <option :value="10">10</option>
                                <option :value="25">25</option>
                                <option :value="50">50</option>
                                <option :value="100">100</option>
                              </select>
                              <label class="ms-2 text-nowrap small">entries</label>
                            </div>
                            <div class="search-box mb-0">
                              <div class="position-relative">
                                <input type="text" v-model="searchTerm"
                                       class="form-control bg-light border-light rounded"
                                       placeholder="Search agents, facilities, regions..." @input="handleSearch">
                                <i class="bx bx-search-alt search-icon"></i>
                              </div>
                            </div>
                            <button type="button" class="btn btn-sm btn-outline-secondary" @click="refreshData" :disabled="loading">
                              <i class="bx bx-refresh" :class="{'bx-spin': loading}"></i>
                            </button>
                          </div>
                          <div>
                            <button type="button" class="btn btn-primary text-capitalize" @click="downloadReport">
                              <i class="bx bxs-download"></i>
                              <span class="ms-1 d-none d-md-inline">Download Report</span>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="card-body">
                        <div>
                          <h4 class="card-title text-uppercase">
                            Collections by Hospitals and Assigned Agents
                            <span class="text-primary active-date">{{ formatDateRangeDisplay(startDate, endDate) }}</span>
                          </h4>
                          <p>{{ getFilterDescription() }}</p>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loading" class="text-center py-5">
                          <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                          </div>
                          <p class="mt-2">Loading agent data...</p>
                        </div>

                        <!-- Error State -->
                        <div v-else-if="error" class="alert alert-danger" role="alert">
                          <i class="bx bx-error-circle me-2"></i>{{ error }}
                        </div>

                        <!-- No Data State -->
                        <div v-else-if="agentCollections.length === 0" class="text-center py-5">
                          <i class="bx bx-info-circle display-1 text-muted"></i>
                          <p class="mt-3 text-muted">No agent collections found for the selected date range and filters.</p>
                        </div>

                        <!-- Data Table -->
                        <div v-else class="table-responsive">
                          <table class="table align-middle table-hover border table-striped text-nowrap mb-0">
                            <thead class="table-light">
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">Agent Name</th>
                              <th scope="col">System Role</th>
                              <th scope="col">Title</th>
                              <th scope="col">Facility Name</th>
                              <th scope="col">Region</th>
                              <th scope="col">Woreda</th>
                              <th scope="col" class="text-center">Transactions</th>
                              <th scope="col" class="text-right">Daily Collection</th>
                              <th scope="col" class="text-right">Total Collection</th>
                              <th scope="col" class="text-center">Unique Members</th>
                              <th scope="col" class="text-center">Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(agent, index) in agentCollections" :key="agent.agent_id">
                              <td>{{ getRowNumber(index) }}</td>
                              <td>{{ agent.agent_username }}</td>
                              <td>{{ agent.system_role }}</td>
                              <td>{{ agent.title }}</td>
                              <td>{{ agent.facility_name }}</td>
                              <td>{{ agent.region_name }}</td>
                              <td>{{ agent.woreda_name }}</td>
                              <td class="text-center">{{ agent.transaction_count }}</td>
                              <td class="text-right">{{ formatCurrency(agent.daily_collection) }} ETB</td>
                              <td class="text-right">{{ formatCurrency(agent.total_collection) }} ETB</td>
                              <td class="text-center">{{ agent.unique_members }}</td>
                              <td class="text-center">
                                <span :class="['badge', 'rounded-pill', 'fw-bold', agent.settled === 1 || agent.settled === '1' ? 'bg-success' : 'bg-danger']">
                                  {{ agent.settled === 1 || agent.settled === '1' ? 'Settled' : 'Pending' }}
                                </span>
                              </td>
                            </tr>
                            </tbody>
                            <tfoot class="table-light text-uppercase" v-if="agentSummary">
                            <tr>
                              <th colspan="7">Total Agents: {{ agentSummary.total_agents }}</th>
                              <th class="text-center">{{ agentSummary.total_transactions }}</th>
                              <th class="text-right">-</th>
                              <th class="text-right">{{ formatCurrency(agentSummary.total_collected) }} ETB</th>
                              <th class="text-center">{{ agentSummary.total_members }}</th>
                              <th class="text-center">
                                <span class="badge rounded-pill bg-info fw-bold">
                                  {{ getAgentSettledPercentage() }}% Settled
                                </span>
                              </th>
                            </tr>
                            </tfoot>
                          </table>

                          <!-- Pagination -->
                          <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.total > 0">
                            <div class="text-muted small">
                              Showing {{ showingFrom }} to {{ showingTo }} of {{ pagination.total }} entries
                            </div>
                            <nav aria-label="Page navigation">
                              <ul class="pagination pagination-sm mb-0">
                                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(1)" title="First">
                                    <i class="bx bx-chevrons-left"></i>
                                  </a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                  <a class="page-link" href="#" @click.prevent="previousPage" title="Previous">
                                    <i class="bx bx-chevron-left"></i>
                                  </a>
                                </li>
                                <li class="page-item" v-for="page in visiblePages" :key="page"
                                    :class="{ active: page === pagination.current_page }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                                  <a class="page-link" href="#" @click.prevent="nextPage" title="Next">
                                    <i class="bx bx-chevron-right"></i>
                                  </a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(pagination.total_pages)" title="Last">
                                    <i class="bx bx-chevrons-right"></i>
                                  </a>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Member Deposits Tab -->
                    <div class="tab-pane" :class="{'active show': activeTab === 'memberDeposits'}" id="activity" role="tabpanel">
                      <div class="card-header bg-white border-bottom">
                        <!-- Filter Bar -->
                        <div class="row g-2 mb-3">
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.regionId" @change="onRegionChange">
                              <option :value="null">All Regions</option>
                              <option v-for="region in regions" :key="region.region_id" :value="region.region_id">
                                {{ region.region_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.zoneId" @change="onZoneChange" :disabled="!filters.regionId">
                              <option :value="null">All Zones</option>
                              <option v-for="zone in zones" :key="zone.zone_id" :value="zone.zone_id">
                                {{ zone.zone_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.woredaId" @change="onWoredaChange" :disabled="!filters.zoneId">
                              <option :value="null">All Woredas</option>
                              <option v-for="woreda in woredas" :key="woreda.woreda_id" :value="woreda.woreda_id">
                                {{ woreda.woreda_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.paymentMethodId" @change="debouncedFetchReportData">
                              <option :value="null">All Payment Methods</option>
                              <option v-for="method in paymentMethods" :key="method.id" :value="method.id">
                                {{ method.name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-1 d-flex align-items-center">
                            <button v-if="hasActiveFilters" type="button" class="btn btn-sm btn-link text-danger p-0" @click="clearAllFilters" title="Clear all filters">
                              <i class="bx bx-x-circle"></i> Clear
                            </button>
                          </div>
                        </div>

                        <!-- Search and Download Bar -->
                        <div class="d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center gap-3">
                            <div class="d-flex align-items-center">
                              <label class="me-2 text-nowrap small">Show</label>
                              <select class="form-select form-select-sm" style="width: 80px;" v-model="perPage" @change="onPerPageChange">
                                <option :value="10">10</option>
                                <option :value="25">25</option>
                                <option :value="50">50</option>
                                <option :value="100">100</option>
                              </select>
                              <label class="ms-2 text-nowrap small">entries</label>
                            </div>
                            <div class="search-box mb-0">
                              <div class="position-relative">
                                <input type="text" v-model="searchTerm"
                                       class="form-control bg-light border-light rounded"
                                       placeholder="Search members, facilities..." @input="handleSearch">
                                <i class="bx bx-search-alt search-icon"></i>
                              </div>
                            </div>
                            <button type="button" class="btn btn-sm btn-outline-secondary" @click="refreshData" :disabled="loading">
                              <i class="bx bx-refresh" :class="{'bx-spin': loading}"></i>
                            </button>
                          </div>
                          <div>
                            <button type="button" class="btn btn-primary text-capitalize" @click="downloadReport">
                              <i class="bx bxs-download"></i>
                              <span class="ms-1 d-none d-md-inline">Download Report</span>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="card-body">
                        <div>
                          <h4 class="card-title text-uppercase">
                            Member Premium Deposits Report
                            <span class="text-primary active-date">{{ formatDateRangeDisplay(startDate, endDate) }}</span>
                          </h4>
                          <p>{{ getFilterDescription() }}</p>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loading" class="text-center py-5">
                          <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                          </div>
                          <p class="mt-2">Loading member deposits...</p>
                        </div>

                        <!-- Error State -->
                        <div v-else-if="error" class="alert alert-danger" role="alert">
                          <i class="bx bx-error-circle me-2"></i>{{ error }}
                        </div>

                        <!-- No Data State -->
                        <div v-else-if="memberDeposits.length === 0" class="text-center py-5">
                          <i class="bx bx-info-circle display-1 text-muted"></i>
                          <p class="mt-3 text-muted">No member deposits found for the selected date range and filters.</p>
                        </div>

                        <!-- Data Table -->
                        <div v-else class="table-responsive">
                          <table class="table align-middle table-hover border table-striped text-nowrap mb-0">
                            <thead class="table-light">
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">CBHI Number.</th>
                              <th scope="col">Member</th>
                              <th scope="col">Region</th>
                              <th scope="col">Woreda</th>
                              <th scope="col">Facility</th>
                              <th scope="col">Payment Method</th>
                              <th scope="col">Channel</th>
                              <th scope="col">Premium Year</th>
                              <th scope="col">Status</th>
                              <th scope="col">Family</th>
                              <th scope="col">Annual Fee</th>
                              <th scope="col">Reg. Fee</th>
                              <th scope="col">Total</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(deposit, index) in memberDeposits" :key="deposit.payment_id">
                              <td>{{ getRowNumber(index) }}</td>
                              <td>{{ deposit.cbhi_number }}</td>
                              <td>{{ deposit.member_name }}</td>
                              <td>{{ deposit.region_name }}</td>
                              <td>{{ deposit.woreda_name }}</td>
                              <td>{{ deposit.facility_name || '-' }}</td>
                              <td>{{ deposit.payment_method }}</td>
                              <td>{{ deposit.channel }}</td>
                              <td>{{ deposit.premium_year }}</td>
                              <td>
                                <span :class="['badge', 'rounded-pill', 'fw-bold', deposit.status === 'active' ? 'bg-success' : 'bg-danger']">
                                  {{ deposit.status }}
                                </span>
                              </td>
                              <td>{{ deposit.family_size }}</td>
                              <td class="text-right">{{ formatCurrency(deposit.annual_fee) }}</td>
                              <td class="text-right">{{ deposit.registration_fee ? formatCurrency(deposit.registration_fee) + ' ETB' : '-' }}</td>
                              <td class="text-right">{{ formatCurrency(deposit.total_amount) }}</td>
                            </tr>
                            </tbody>
                            <tfoot class="table-light text-uppercase" v-if="memberDepositsSummary">
                            <tr>
                              <th colspan="11">Total Members: {{ memberDepositsSummary.total_deposits }}</th>
                              <th class="text-right">{{ formatCurrency(memberDepositsSummary.total_annual_fees) }}</th>
                              <th class="text-right">{{ formatCurrency(memberDepositsSummary.total_reg_fees) }}</th>
                              <th class="text-right">{{ formatCurrency(memberDepositsSummary.total_amount) }}</th>
                            </tr>
                            </tfoot>
                          </table>

                          <!-- Pagination -->
                          <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.total > 0">
                            <div class="text-muted small">
                              Showing {{ showingFrom }} to {{ showingTo }} of {{ pagination.total }} entries
                            </div>
                            <nav aria-label="Page navigation">
                              <ul class="pagination pagination-sm mb-0">
                                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(1)" title="First">
                                    <i class="bx bx-chevrons-left"></i>
                                  </a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                  <a class="page-link" href="#" @click.prevent="previousPage" title="Previous">
                                    <i class="bx bx-chevron-left"></i>
                                  </a>
                                </li>
                                <li class="page-item" v-for="page in visiblePages" :key="page"
                                    :class="{ active: page === pagination.current_page }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                                  <a class="page-link" href="#" @click.prevent="nextPage" title="Next">
                                    <i class="bx bx-chevron-right"></i>
                                  </a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(pagination.total_pages)" title="Last">
                                    <i class="bx bx-chevrons-right"></i>
                                  </a>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Woreda Collections Tab -->
                    <div class="tab-pane" :class="{'active show': activeTab === 'woredaCollections'}" id="woreda" role="tabpanel">
                      <div class="card-header bg-white border-bottom">
                        <!-- Filter Bar -->
                        <div class="row g-2 mb-3">
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.regionId" @change="onRegionChange">
                              <option :value="null">All Regions</option>
                              <option v-for="region in regions" :key="region.region_id" :value="region.region_id">
                                {{ region.region_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.zoneId" @change="onZoneChange" :disabled="!filters.regionId">
                              <option :value="null">All Zones</option>
                              <option v-for="zone in zones" :key="zone.zone_id" :value="zone.zone_id">
                                {{ zone.zone_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-2">
                            <select class="form-select form-select-sm" v-model="filters.woredaId" @change="onWoredaChange" :disabled="!filters.zoneId">
                              <option :value="null">All Woredas</option>
                              <option v-for="woreda in woredas" :key="woreda.woreda_id" :value="woreda.woreda_id">
                                {{ woreda.woreda_name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-md-1 d-flex align-items-center">
                            <button v-if="hasActiveFilters" type="button" class="btn btn-sm btn-link text-danger p-0" @click="clearAllFilters" title="Clear all filters">
                              <i class="bx bx-x-circle"></i> Clear
                            </button>
                          </div>
                        </div>

                        <!-- Search and Action Buttons Bar -->
                        <div class="d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center gap-3">
                            <div class="d-flex align-items-center">
                              <label class="me-2 text-nowrap small">Show</label>
                              <select class="form-select form-select-sm" style="width: 80px;" v-model="perPage" @change="onPerPageChange">
                                <option :value="10">10</option>
                                <option :value="25">25</option>
                                <option :value="50">50</option>
                                <option :value="100">100</option>
                              </select>
                              <label class="ms-2 text-nowrap small">entries</label>
                            </div>
                            <div class="search-box mb-0">
                              <div class="position-relative">
                                <input type="text" v-model="searchTerm"
                                       class="form-control bg-light border-light rounded"
                                       placeholder="Search woredas, regions..." @input="handleSearch">
                                <i class="bx bx-search-alt search-icon"></i>
                              </div>
                            </div>
                            <button type="button" class="btn btn-sm btn-outline-secondary" @click="refreshData" :disabled="loading">
                              <i class="bx bx-refresh" :class="{'bx-spin': loading}"></i>
                            </button>
                          </div>
                          <div class="d-flex gap-2">
                            <button type="button" class="btn btn-success text-capitalize" @click="uploadBankStatement">
                              <i class="bx bx-upload"></i>
                              <span class="ms-1 d-none d-md-inline">Upload Bank Statement</span>
                            </button>
                            <button type="button" class="btn btn-primary text-capitalize" @click="downloadReport">
                              <i class="bx bxs-download"></i>
                              <span class="ms-1 d-none d-md-inline">Download Report</span>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="card-body">
                        <div>
                          <h4 class="card-title text-uppercase">
                            Collections vs Settlements Report
                            <span class="text-primary active-date">{{ formatDateRangeDisplay(startDate, endDate) }}</span>
                          </h4>
                          <p>{{ getFilterDescription() }}</p>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loading" class="text-center py-5">
                          <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                          </div>
                          <p class="mt-2">Loading woreda collections...</p>
                        </div>

                        <!-- Error State -->
                        <div v-else-if="error" class="alert alert-danger" role="alert">
                          <i class="bx bx-error-circle me-2"></i>{{ error }}
                        </div>

                        <!-- No Data State -->
                        <div v-else-if="woredaCollections.length === 0" class="text-center py-5">
                          <i class="bx bx-info-circle display-1 text-muted"></i>
                          <p class="mt-3 text-muted">No collections found for the selected date range and filters.</p>
                        </div>

                        <!-- Data Table -->
                        <div v-else class="table-responsive">
                          <table class="table align-middle table-hover border table-striped text-nowrap mb-0">
                            <thead class="table-light">
                            <tr>
                              <th scope="col">Reference Number</th>
                              <th scope="col">Region</th>
                              <th scope="col">Woreda Name</th>
                              <th scope="col">Kebele</th>
                              <th scope="col">Bank</th>
                              <th scope="col">Bank Account</th>
                              <th scope="col">Settlement Date & Time</th>
                              <th scope="col" class="text-right">Amount Collected (ETB)</th>
                              <th scope="col" class="text-right"><strong>Amount Settled (ETB)</strong></th>
                              <th scope="col" class="text-right">Amount Pending (ETB)</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(collection, index) in woredaCollections"
                                :key="collection.reference_number"
                                :class="index % 2 === 1 ? 'table-primary' : ''">
                              <td>{{ collection.reference_number }}</td>
                              <td>{{ collection.region_name }}</td>
                              <td>{{ collection.woreda_name }}</td>
                              <td>{{ collection.kebele_count }}</td>
                              <td>{{ collection.institution_name }}</td>
                              <td>{{ collection.bank_details }}</td>
                              <td>{{ formatDateDisplay(collection.settlement_date) }}</td>
                              <td class="text-right">{{ formatCurrency(collection.amount_collected) }}</td>
                              <td class="text-right"><strong>{{ formatCurrency(collection.amount_settled_to_hospital) }}</strong></td>
                              <td class="text-right">{{ formatCurrency(collection.amount_awaiting_settlement) }}</td>
                            </tr>
                            </tbody>
                            <tfoot class="table-light text-uppercase" v-if="woredaSummary">
                            <tr>
                              <th colspan="2">Totals:</th>
                              <th colspan="5">{{ woredaSummary.total_woredas }} Woredas across {{ woredaSummary.total_regions }} Regions</th>
                              <th class="text-right">{{ formatCurrency(woredaSummary.total_collected) }}</th>
                              <th class="text-right">{{ formatCurrency(woredaSummary.total_settled) }}</th>
                              <th class="text-right">{{ formatCurrency(woredaSummary.total_pending) }}</th>
                            </tr>
                            </tfoot>
                          </table>

                          <!-- Pagination -->
                          <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.total > 0">
                            <div class="text-muted small">
                              Showing {{ showingFrom }} to {{ showingTo }} of {{ pagination.total }} entries
                            </div>
                            <nav aria-label="Page navigation">
                              <ul class="pagination pagination-sm mb-0">
                                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(1)" title="First">
                                    <i class="bx bx-chevrons-left"></i>
                                  </a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                  <a class="page-link" href="#" @click.prevent="previousPage" title="Previous">
                                    <i class="bx bx-chevron-left"></i>
                                  </a>
                                </li>
                                <li class="page-item" v-for="page in visiblePages" :key="page"
                                    :class="{ active: page === pagination.current_page }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                                  <a class="page-link" href="#" @click.prevent="nextPage" title="Next">
                                    <i class="bx bx-chevron-right"></i>
                                  </a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                                  <a class="page-link" href="#" @click.prevent="goToPage(pagination.total_pages)" title="Last">
                                    <i class="bx bx-chevrons-right"></i>
                                  </a>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Channels Tab -->
                    <div class="tab-pane" :class="{'active show': activeTab === 'channels'}" id="channels" role="tabpanel">
                      <div class="card-header bg-white border-bottom">
                        <div class="d-flex justify-content-between align-items-center">
                          <h4 class="card-title mb-0">Collections Breakdown by Channels and Payment Methods</h4>
                          <button type="button" class="btn btn-primary text-capitalize" @click="downloadReport">
                            <i class="bx bxs-download"></i>
                            <span class="ms-1 d-none d-md-inline">Download Report</span>
                          </button>
                        </div>
                      </div>

                      <div class="card-body">
                        <div>
                          <h4 class="card-title text-uppercase">
                            Channel Analytics Report
                            <span class="text-primary active-date">{{ formatDateRangeDisplay(startDate, endDate) }}</span>
                          </h4>
                          <p>Payment channel performance and transaction breakdown</p>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loading" class="text-center py-5">
                          <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                          </div>
                          <p class="mt-2">Loading channel analytics...</p>
                        </div>

                        <!-- Error State -->
                        <div v-else-if="error" class="alert alert-danger" role="alert">
                          <i class="bx bx-error-circle me-2"></i>{{ error }}
                        </div>

                        <!-- No Data State -->
                        <div v-else-if="channelStats.length === 0" class="text-center py-5">
                          <i class="bx bx-info-circle display-1 text-muted"></i>
                          <p class="mt-3 text-muted">No channel data found for the selected date range.</p>
                        </div>

                        <!-- Channel Data Display -->
                        <div v-else>
                          <!-- Summary Cards Row -->
                          <div class="row g-4 mb-4">
                            <div class="col-lg-3 col-md-6" v-for="(channel, index) in channelStats" :key="index">
                              <div class="card stats-card border">
                                <div class="card-body">
                                  <div class="d-flex flex-wrap">
                                    <div class="me-3 flex-grow-1">
                                      <p class="stat-label text-uppercase fw-bold">{{ channel.channel_name }}</p>
                                      <h3 class="stat-value">{{ formatCurrency(channel.total_amount) }} <span class="currency">ETB</span></h3>
                                      <div class="stat-change positive">
                                        <i class="bx bx-trending-up me-1"></i>{{ channel.transaction_count }} transactions ({{ channel.percentage }}%)
                                      </div>
                                      <div class="mt-2">
                                        <small class="text-muted">
                                          Avg: {{ formatCurrency(channel.avg_amount) }} ETB |
                                          {{ channel.unique_members }} unique members
                                        </small>
                                      </div>
                                    </div>
                                    <div class="avatar-sm ms-auto">
                                      <div class="avatar-title bg-primary rounded-circle text-white font-size-20">
                                        <i class="bx bx-credit-card"></i>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Detailed Channel Table -->
                          <div class="table-responsive">
                            <table class="table align-middle table-hover border table-striped text-nowrap mb-0">
                              <thead class="table-light">
                              <tr>
                                <th scope="col">#</th>
                                <th scope="col">Channel Name</th>
                                <th scope="col">Payment Method</th>
                                <th scope="col" class="text-center">Transactions</th>
                                <th scope="col" class="text-center">Unique Members</th>
                                <th scope="col" class="text-right">Total Amount (ETB)</th>
                                <th scope="col" class="text-right">Min Amount (ETB)</th>
                                <th scope="col" class="text-right">Max Amount (ETB)</th>
                                <th scope="col" class="text-right">Avg Amount (ETB)</th>
                                <th scope="col" class="text-center">Transaction %</th>
                                <th scope="col" class="text-center">Amount %</th>
                              </tr>
                              </thead>
                              <tbody>
                              <tr v-for="(channel, index) in channelStats" :key="index">
                                <td>{{ index + 1 }}</td>
                                <td>
                                  <span class="badge bg-primary rounded-pill">{{ channel.channel_name }}</span>
                                </td>
                                <td>{{ channel.method_name }}</td>
                                <td class="text-center">
                                  <span class="fw-bold">{{ channel.transaction_count }}</span>
                                </td>
                                <td class="text-center">{{ channel.unique_members }}</td>
                                <td class="text-right fw-bold">{{ formatCurrency(channel.total_amount) }}</td>
                                <td class="text-right">{{ formatCurrency(channel.min_amount) }}</td>
                                <td class="text-right">{{ formatCurrency(channel.max_amount) }}</td>
                                <td class="text-right">{{ formatCurrency(channel.avg_amount) }}</td>
                                <td class="text-center">
                                  <span class="badge bg-success rounded-pill">{{ channel.percentage }}%</span>
                                </td>
                                <td class="text-center">
                                  <span class="badge bg-info rounded-pill">{{ channel.amount_percentage }}%</span>
                                </td>
                              </tr>
                              </tbody>
                              <tfoot class="table-light text-uppercase">
                              <tr>
                                <th colspan="3">Total:</th>
                                <th class="text-center">{{ getChannelTotalTransactions() }}</th>
                                <th class="text-center">{{ getChannelTotalMembers() }}</th>
                                <th class="text-right">{{ formatCurrency(getChannelTotalAmount()) }} ETB</th>
                                <th colspan="4" class="text-center">
                                  <span class="badge bg-primary rounded-pill">{{ channelStats.length }} Channel(s)</span>
                                </th>
                              </tr>
                              </tfoot>
                            </table>
                          </div>

                          <!-- Channel Summary Section -->
                          <div class="mt-4" v-if="channelSummary && channelSummary.length > 0">
                            <h5 class="text-uppercase mb-3">Channel Summary</h5>
                            <div class="row">
                              <div class="col-md-6" v-for="(summary, index) in channelSummary" :key="index">
                                <div class="card border">
                                  <div class="card-body">
                                    <h6 class="card-title text-primary">{{ summary.channel_type }}</h6>
                                    <p class="card-text mb-2">
                                      <strong>Payment Methods:</strong> {{ summary.methods.join(', ') }}
                                    </p>
                                    <div class="d-flex justify-content-between">
                                      <span><strong>Transactions:</strong> {{ summary.total_transactions }}</span>
                                      <span><strong>Amount:</strong> {{ formatCurrency(summary.total_amount) }} ETB</span>
                                    </div>
                                    <div class="progress mt-2" style="height: 8px;">
                                      <div class="progress-bar bg-primary" role="progressbar"
                                           :style="`width: ${summary.percentage}%`"
                                           :aria-valuenow="summary.percentage"
                                           aria-valuemin="0"
                                           aria-valuemax="100">
                                      </div>
                                    </div>
                                    <small class="text-muted">{{ summary.percentage }}% of total collections</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">
              © CBHI
            </div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import {authUrl, executeJson, getRight} from "@/api";

export default {
  name: "PremiumCollectionsReports",
  components: { NavigationBar },
  data() {
    return {
      // API loading states
      loading: false,
      error: null,

      // Ethiopian Calendar State
      showEthiopianDates: true,
      currentEthiopianDate: { year: 2017, month: 3, day: 15, monthName: 'ህዳር', monthNameEn: 'Hidar' },

      // Date Filter Range (works for both Ethiopian and Gregorian)
      dateFilterRange: {
        fromDay: '',
        fromMonth: '',
        fromYear: '',
        toDay: '',
        toMonth: '',
        toYear: ''
      },

      // Gregorian dates for API calls
      startDate: '',
      endDate: '',
      maxDate: new Date().toISOString().split('T')[0],

      // Ethiopian months list
      ethiopianMonths: [
        { value: 1, label: 'መስከረም', labelEn: 'Meskerem' },
        { value: 2, label: 'ጥቅምት', labelEn: 'Tikimt' },
        { value: 3, label: 'ህዳር', labelEn: 'Hidar' },
        { value: 4, label: 'ታህሳስ', labelEn: 'Tahsas' },
        { value: 5, label: 'ጥር', labelEn: 'Tir' },
        { value: 6, label: 'የካቲት', labelEn: 'Yekatit' },
        { value: 7, label: 'መጋቢት', labelEn: 'Megabit' },
        { value: 8, label: 'ሚያዝያ', labelEn: 'Miyazya' },
        { value: 9, label: 'ግንቦት', labelEn: 'Ginbot' },
        { value: 10, label: 'ሰኔ', labelEn: 'Sene' },
        { value: 11, label: 'ሐምሌ', labelEn: 'Hamle' },
        { value: 12, label: 'ነሐሴ', labelEn: 'Nehase' },
        { value: 13, label: 'ጳጉሜ', labelEn: 'Pagume' }
      ],

      // Gregorian months list
      gregorianMonths: [
        { value: 1, label: 'January', labelShort: 'Jan' },
        { value: 2, label: 'February', labelShort: 'Feb' },
        { value: 3, label: 'March', labelShort: 'Mar' },
        { value: 4, label: 'April', labelShort: 'Apr' },
        { value: 5, label: 'May', labelShort: 'May' },
        { value: 6, label: 'June', labelShort: 'Jun' },
        { value: 7, label: 'July', labelShort: 'Jul' },
        { value: 8, label: 'August', labelShort: 'Aug' },
        { value: 9, label: 'September', labelShort: 'Sep' },
        { value: 10, label: 'October', labelShort: 'Oct' },
        { value: 11, label: 'November', labelShort: 'Nov' },
        { value: 12, label: 'December', labelShort: 'Dec' }
      ],

      // Active tab - set to agents by default
      activeTab: 'agents',

      // Search and filters
      searchTerm: '',
      filters: {
        regionId: null,
        zoneId: null,
        woredaId: null,
        hospitalId: null,
        categoryId: null,
        status: null,
        paymentMethodId: null,
        channelId: null
      },

      // Pagination
      perPage: 10,
      currentPage: 1,
      pagination: {
        total: 0,
        per_page: 10,
        current_page: 1,
        total_pages: 1,
        offset: 0,
        has_more: false
      },

      // Location data
      regions: [],
      zones: [],
      woredas: [],
      kebeles: [],
      hospitals: [],
      paymentMethods: [],

      // Dashboard statistics
      dashboardStats: {
        totalCollections: 0,
        collectionChange: 0,
        settlementRate: 0,
        settledAmount: 0,
        collectionEfficiency: 0,
        memberDeposits: 0,
        newDeposits: 0
      },

      // Report data - separate for each tab
      hospitalCollections: [],
      hospitalSummary: null,
      agentCollections: [],
      agentSummary: null,
      memberDeposits: [],
      memberDepositsSummary: null,
      woredaCollections: [],
      woredaSummary: null,
      channelStats: [],
      channelSummary: [],

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: '',

      // Debounce timers
      searchDebounceTimer: null,
      filterDebounceTimer: null,
      dateRangeDebounceTimer: null
    };
  },

  computed: {
    hasAnyReportPermission() {
      return this.getRight('REPORTS HOSPITALS') ||
          this.getRight('REPORTS AGENTS') ||
          this.getRight('REPORTS MEMBER DEPOSITS') ||
          this.getRight('REPORTS WOREDA COLLECTIONS') ||
          this.getRight('REPORTS CHANNELS');
    },

    hasActiveFilters() {
      return this.filters.regionId || this.filters.zoneId || this.filters.woredaId ||
          this.filters.hospitalId || this.filters.paymentMethodId || this.filters.channelId;
    },

    // Ethiopian year options
    ethiopianYearOptions() {
      const years = [];
      for (let y = this.currentEthiopianDate.year - 20; y <= this.currentEthiopianDate.year + 5; y++) {
        years.push(y);
      }
      return years;
    },

    // Gregorian year options
    gregorianYearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear - 20; y <= currentYear + 5; y++) {
        years.push(y);
      }
      return years;
    },

    // Display months based on calendar type
    displayMonths() {
      return this.showEthiopianDates ? this.ethiopianMonths : this.gregorianMonths;
    },

    // Display years based on calendar type
    displayYearOptions() {
      return this.showEthiopianDates ? this.ethiopianYearOptions : this.gregorianYearOptions;
    },

    // Days in FROM month
    fromDaysInMonth() {
      if (this.showEthiopianDates) {
        return this.getDaysInEthiopianMonth(this.dateFilterRange.fromMonth, this.dateFilterRange.fromYear);
      } else {
        return this.getDaysInGregorianMonth(this.dateFilterRange.fromMonth, this.dateFilterRange.fromYear);
      }
    },

    // Days in TO month
    toDaysInMonth() {
      if (this.showEthiopianDates) {
        return this.getDaysInEthiopianMonth(this.dateFilterRange.toMonth, this.dateFilterRange.toYear);
      } else {
        return this.getDaysInGregorianMonth(this.dateFilterRange.toMonth, this.dateFilterRange.toYear);
      }
    },

    // Check if any date is selected
    hasDateSelection() {
      return (this.dateFilterRange.fromDay && this.dateFilterRange.fromMonth && this.dateFilterRange.fromYear) ||
          (this.dateFilterRange.toDay && this.dateFilterRange.toMonth && this.dateFilterRange.toYear);
    },

    // Check if date range is valid
    isValidDateRange() {
      return this.dateFilterRange.fromDay && this.dateFilterRange.fromMonth && this.dateFilterRange.fromYear &&
          this.dateFilterRange.toDay && this.dateFilterRange.toMonth && this.dateFilterRange.toYear;
    },

    // Display alternate calendar date (Ethiopian shows Gregorian, Gregorian shows Ethiopian)
    alternateDateDisplay() {
      if (!this.isValidDateRange) return '';

      if (this.showEthiopianDates) {
        // Currently Ethiopian, show Gregorian equivalent
        const fromGreg = this.ethiopianToGregorian(
            parseInt(this.dateFilterRange.fromYear),
            parseInt(this.dateFilterRange.fromMonth),
            parseInt(this.dateFilterRange.fromDay)
        );
        const toGreg = this.ethiopianToGregorian(
            parseInt(this.dateFilterRange.toYear),
            parseInt(this.dateFilterRange.toMonth),
            parseInt(this.dateFilterRange.toDay)
        );

        if (!fromGreg || !toGreg) return '';

        const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${formatDate(fromGreg)} — ${formatDate(toGreg)}`;
      } else {
        // Currently Gregorian, show Ethiopian equivalent
        const fromEth = this.gregorianToEthiopian(new Date(
            parseInt(this.dateFilterRange.fromYear),
            parseInt(this.dateFilterRange.fromMonth) - 1,
            parseInt(this.dateFilterRange.fromDay)
        ));
        const toEth = this.gregorianToEthiopian(new Date(
            parseInt(this.dateFilterRange.toYear),
            parseInt(this.dateFilterRange.toMonth) - 1,
            parseInt(this.dateFilterRange.toDay)
        ));

        if (!fromEth || !toEth) return '';

        return `${fromEth.monthNameEn} ${fromEth.day}, ${fromEth.year} — ${toEth.monthNameEn} ${toEth.day}, ${toEth.year}`;
      }
    },

    // Pagination computed properties
    showingFrom() {
      if (this.pagination.total === 0) return 0;
      return (this.pagination.current_page - 1) * this.pagination.per_page + 1;
    },

    showingTo() {
      const to = this.pagination.current_page * this.pagination.per_page;
      return Math.min(to, this.pagination.total);
    },

    visiblePages() {
      const totalPages = this.pagination.total_pages;
      const current = this.pagination.current_page;
      const pages = [];

      if (totalPages <= 5) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        let start = Math.max(1, current - 2);
        let end = Math.min(totalPages, current + 2);

        if (current <= 3) {
          end = 5;
        }
        if (current >= totalPages - 2) {
          start = totalPages - 4;
        }

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      }

      return pages;
    }
  },

  watch: {
    // Clear date filter selections when calendar type is toggled
    showEthiopianDates() {
      this.dateFilterRange = { fromDay: '', fromMonth: '', fromYear: '', toDay: '', toMonth: '', toYear: '' };
    }
  },

  mounted() {
    this.currentEthiopianDate = this.getCurrentEthiopianDate();
    this.initializeData();
  },

  methods: {
    getRight,

    // ==================== ETHIOPIAN CALENDAR METHODS ====================

    // Get days in Ethiopian month
    getDaysInEthiopianMonth(month, year) {
      if (!month || !year) return 30;
      const m = parseInt(month);
      const y = parseInt(year);
      if (m === 13) {
        return (y % 4 === 3) ? 6 : 5; // Pagume: 6 days in leap year, 5 otherwise
      }
      return 30;
    },

    // Get days in Gregorian month
    getDaysInGregorianMonth(month, year) {
      if (!month || !year) return 31;
      const m = parseInt(month);
      const y = parseInt(year);

      if ([1, 3, 5, 7, 8, 10, 12].includes(m)) return 31;
      if ([4, 6, 9, 11].includes(m)) return 30;
      if (m === 2) {
        const isLeap = (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
        return isLeap ? 29 : 28;
      }
      return 31;
    },

    // Ethiopian to Gregorian conversion
    ethiopianToGregorian(ethYear, ethMonth, ethDay) {
      const ETHIOPIAN_EPOCH = 1724221;

      const jdn = ETHIOPIAN_EPOCH +
          365 * (ethYear - 1) +
          Math.floor(ethYear / 4) +
          30 * (ethMonth - 1) +
          ethDay - 1;

      const a = jdn + 32044;
      const b = Math.floor((4 * a + 3) / 146097);
      const c = a - Math.floor(146097 * b / 4);
      const d = Math.floor((4 * c + 3) / 1461);
      const e = c - Math.floor(1461 * d / 4);
      const m = Math.floor((5 * e + 2) / 153);

      const day = e - Math.floor((153 * m + 2) / 5) + 1;
      const month = m + 3 - 12 * Math.floor(m / 10);
      const year = 100 * b + d - 4800 + Math.floor(m / 10);

      return new Date(year, month - 1, day);
    },

    // Gregorian to Ethiopian conversion
    gregorianToEthiopian(gregDate) {
      const date = typeof gregDate === 'string' ? new Date(gregDate) : gregDate;
      if (!date || isNaN(date.getTime())) return null;

      const gYear = date.getFullYear();
      const gMonth = date.getMonth() + 1;
      const gDay = date.getDate();

      const a = Math.floor((14 - gMonth) / 12);
      const y = gYear + 4800 - a;
      const m = gMonth + 12 * a - 3;
      const jdn = gDay + Math.floor((153 * m + 2) / 5) + 365 * y +
          Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;

      const ETHIOPIAN_EPOCH = 1724221;
      const r = (jdn - ETHIOPIAN_EPOCH) % 1461;
      const n = r % 365 + 365 * Math.floor(r / 1460);

      const ethYear = 4 * Math.floor((jdn - ETHIOPIAN_EPOCH) / 1461) +
          Math.floor(r / 365) - Math.floor(r / 1460) + 1;
      const ethMonth = Math.floor(n / 30) + 1;
      const ethDay = n % 30 + 1;

      const monthData = this.ethiopianMonths.find(mon => mon.value === ethMonth);

      return {
        year: ethYear,
        month: ethMonth,
        day: ethDay,
        monthName: monthData?.label || '',
        monthNameEn: monthData?.labelEn || ''
      };
    },

    // Get current Ethiopian date
    getCurrentEthiopianDate() {
      return this.gregorianToEthiopian(new Date());
    },

    // Format date based on calendar preference
    formatDateDisplay(dateString) {
      if (!dateString || dateString === 'Pending Settlement') return dateString || '-';

      if (this.showEthiopianDates) {
        const eth = this.gregorianToEthiopian(dateString);
        if (eth) {
          return `${eth.monthNameEn} ${eth.day}, ${eth.year}`;
        }
      }

      return this.formatGregorianDate(dateString);
    },

    // Format Gregorian date
    formatGregorianDate(dateString) {
      if (!dateString) return '';
      const d = new Date(dateString);
      return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    // Format date range for display
    formatDateRangeDisplay(startDate, endDate) {
      if (!startDate || !endDate) return '';

      if (this.showEthiopianDates) {
        const startEth = this.gregorianToEthiopian(startDate);
        const endEth = this.gregorianToEthiopian(endDate);

        if (startEth && endEth) {
          if (startDate === endDate) {
            return `${startEth.monthNameEn} ${startEth.day}, ${startEth.year}`;
          }
          return `${startEth.monthNameEn} ${startEth.day}, ${startEth.year} — ${endEth.monthNameEn} ${endEth.day}, ${endEth.year}`;
        }
      }

      // Gregorian format
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (startDate === endDate) {
        return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }

      const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${startStr} — ${endStr}`;
    },

    // Handle FROM month change
    onFromMonthChange() {
      if (this.dateFilterRange.fromDay > this.fromDaysInMonth) {
        this.dateFilterRange.fromDay = this.fromDaysInMonth;
      }
      this.updateDateRange();
    },

    // Handle TO month change
    onToMonthChange() {
      if (this.dateFilterRange.toDay > this.toDaysInMonth) {
        this.dateFilterRange.toDay = this.toDaysInMonth;
      }
      this.updateDateRange();
    },

    // Update date range (convert to Gregorian for API)
    updateDateRange() {
      if (this.dateFilterRange.fromDay && this.dateFilterRange.fromMonth && this.dateFilterRange.fromYear) {
        let fromGreg;
        if (this.showEthiopianDates) {
          fromGreg = this.ethiopianToGregorian(
              parseInt(this.dateFilterRange.fromYear),
              parseInt(this.dateFilterRange.fromMonth),
              parseInt(this.dateFilterRange.fromDay)
          );
        } else {
          fromGreg = new Date(
              parseInt(this.dateFilterRange.fromYear),
              parseInt(this.dateFilterRange.fromMonth) - 1,
              parseInt(this.dateFilterRange.fromDay)
          );
        }
        if (fromGreg) {
          this.startDate = fromGreg.toISOString().split('T')[0];
        }
      }

      if (this.dateFilterRange.toDay && this.dateFilterRange.toMonth && this.dateFilterRange.toYear) {
        let toGreg;
        if (this.showEthiopianDates) {
          toGreg = this.ethiopianToGregorian(
              parseInt(this.dateFilterRange.toYear),
              parseInt(this.dateFilterRange.toMonth),
              parseInt(this.dateFilterRange.toDay)
          );
        } else {
          toGreg = new Date(
              parseInt(this.dateFilterRange.toYear),
              parseInt(this.dateFilterRange.toMonth) - 1,
              parseInt(this.dateFilterRange.toDay)
          );
        }
        if (toGreg) {
          this.endDate = toGreg.toISOString().split('T')[0];
        }
      }
    },

    // Apply date filter
    applyDateFilter() {
      if (this.isValidDateRange) {
        this.updateDateRange();
        this.resetPagination();
        this.fetchDataForActiveTab();
      }
    },

    // Clear date filter
    clearDateFilter() {
      this.dateFilterRange = { fromDay: '', fromMonth: '', fromYear: '', toDay: '', toMonth: '', toYear: '' };
      this.startDate = '';
      this.endDate = '';
      this.resetPagination();
      this.fetchDataForActiveTab();
    },

    // Quick date range presets
    setQuickDateRange(period) {
      const today = new Date();
      let startDate = new Date();
      let endDate = new Date();

      switch(period) {
        case 'today':
          startDate = new Date();
          break;
        case 'week':
          startDate.setDate(today.getDate() - 7);
          break;
        case 'month':
          startDate.setDate(1);
          break;
        case 'quarter':
          const currentMonth = today.getMonth();
          const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
          startDate = new Date(today.getFullYear(), quarterStartMonth, 1);
          break;
      }

      // Set Gregorian dates
      this.startDate = startDate.toISOString().split('T')[0];
      this.endDate = endDate.toISOString().split('T')[0];

      // Update the dropdown selections based on current calendar type
      if (this.showEthiopianDates) {
        const startEth = this.gregorianToEthiopian(startDate);
        const endEth = this.gregorianToEthiopian(endDate);

        if (startEth && endEth) {
          this.dateFilterRange.fromDay = startEth.day;
          this.dateFilterRange.fromMonth = startEth.month;
          this.dateFilterRange.fromYear = startEth.year;
          this.dateFilterRange.toDay = endEth.day;
          this.dateFilterRange.toMonth = endEth.month;
          this.dateFilterRange.toYear = endEth.year;
        }
      } else {
        this.dateFilterRange.fromDay = startDate.getDate();
        this.dateFilterRange.fromMonth = startDate.getMonth() + 1;
        this.dateFilterRange.fromYear = startDate.getFullYear();
        this.dateFilterRange.toDay = endDate.getDate();
        this.dateFilterRange.toMonth = endDate.getMonth() + 1;
        this.dateFilterRange.toYear = endDate.getFullYear();
      }

      this.resetPagination();
      this.fetchDataForActiveTab();
    },

    // ==================== PAGINATION & TAB METHODS ====================

    // Get row number for server-side pagination
    getRowNumber(index) {
      return (this.pagination.current_page - 1) * this.pagination.per_page + index + 1;
    },

    // Tab switching method
    switchTab(tabName) {
      this.activeTab = tabName;
      this.resetPagination();
      this.searchTerm = '';
      this.fetchDataForActiveTab();
    },

    // Reset pagination to first page
    resetPagination() {
      this.currentPage = 1;
      this.pagination = {
        total: 0,
        per_page: this.perPage,
        current_page: 1,
        total_pages: 1,
        offset: 0,
        has_more: false
      };
    },

    // Pagination methods
    previousPage() {
      if (this.pagination.current_page > 1) {
        this.goToPage(this.pagination.current_page - 1);
      }
    },

    nextPage() {
      if (this.pagination.current_page < this.pagination.total_pages) {
        this.goToPage(this.pagination.current_page + 1);
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.total_pages) return;
      this.currentPage = page;
      this.fetchDataForActiveTab();
    },

    onPerPageChange() {
      this.resetPagination();
      this.fetchDataForActiveTab();
    },

    // Refresh data
    refreshData() {
      this.searchTerm = '';
      this.resetPagination();
      this.fetchDataForActiveTab();
    },

    async initializeData() {
      // Set default date to today
      const today = new Date();
      this.startDate = today.toISOString().split('T')[0];
      this.endDate = today.toISOString().split('T')[0];

      // Set Ethiopian date filter defaults
      const todayEth = this.getCurrentEthiopianDate();
      if (todayEth) {
        this.dateFilterRange.fromDay = todayEth.day;
        this.dateFilterRange.fromMonth = todayEth.month;
        this.dateFilterRange.fromYear = todayEth.year;
        this.dateFilterRange.toDay = todayEth.day;
        this.dateFilterRange.toMonth = todayEth.month;
        this.dateFilterRange.toYear = todayEth.year;
      }

      try {
        await this.fetchRegions();
        await this.fetchHospitals();
        await this.fetchPaymentMethods();
        await this.fetchDataForActiveTab();
      } catch (error) {
        this.showError('Failed to initialize data: ' + error.message);
      }
    },

    // Fetch data based on active tab
    async fetchDataForActiveTab() {
      switch(this.activeTab) {
        case 'hospitals':
          await this.fetchHospitalCollections();
          break;
        case 'agents':
          await this.fetchAgentCollections();
          break;
        case 'memberDeposits':
          await this.fetchMemberDeposits();
          break;
        case 'woredaCollections':
          await this.fetchWoredaCollections();
          break;
        case 'channels':
          await this.fetchChannelStats();
          break;
      }
    },

    // ==================== FILTER HANDLERS ====================

    async onRegionChange() {
      this.filters.zoneId = null;
      this.filters.woredaId = null;
      this.zones = [];
      this.woredas = [];

      if (this.filters.regionId) {
        await this.fetchZones();
      }

      this.debouncedFetchReportData();
    },

    async onZoneChange() {
      this.filters.woredaId = null;
      this.woredas = [];

      if (this.filters.zoneId) {
        await this.fetchWoredas();
      }

      this.debouncedFetchReportData();
    },

    async onWoredaChange() {
      this.debouncedFetchReportData();
    },

    async onHospitalChange() {
      this.debouncedFetchReportData();
    },

    // Debounced fetch
    debouncedFetchReportData() {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = setTimeout(() => {
        this.resetPagination();
        this.fetchDataForActiveTab();
      }, 500);
    },

    // Handle search with debounce
    handleSearch() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.resetPagination();
        this.fetchDataForActiveTab();
      }, 300);
    },

    clearAllFilters() {
      this.filters = {
        regionId: null,
        zoneId: null,
        woredaId: null,
        hospitalId: null,
        categoryId: null,
        status: null,
        paymentMethodId: null,
        channelId: null
      };
      this.zones = [];
      this.woredas = [];
      this.searchTerm = '';
      this.resetPagination();
      this.fetchDataForActiveTab();
    },

    // ==================== API CALLS ====================

    async fetchRegions() {
      try {
        const data = { function: "getRegions" };
        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.regions = response.data.data.regions || [];
        }
      } catch (error) {
        console.error('Error fetching regions:', error);
      }
    },

    async fetchZones() {
      if (!this.filters.regionId) {
        this.zones = [];
        this.woredas = [];
        return;
      }

      try {
        const data = {
          function: "getZones",
          regionId: this.filters.regionId
        };
        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.zones = response.data.data.zones || [];
        }
      } catch (error) {
        console.error('Error fetching zones:', error);
      }
    },

    async fetchWoredas() {
      if (!this.filters.zoneId) {
        this.woredas = [];
        return;
      }

      try {
        const data = {
          function: "getWoredas",
          zoneId: this.filters.zoneId
        };
        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.woredas = response.data.data.woredas || [];
        }
      } catch (error) {
        console.error('Error fetching woredas:', error);
      }
    },

    async fetchHospitals() {
      try {
        const data = { function: "getHospitals" };
        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.hospitals = response.data.data.hospitals || [];
        }
      } catch (error) {
        console.error('Error fetching hospitals:', error);
      }
    },

    async fetchPaymentMethods() {
      try {
        const data = { function: "getPaymentMethods" };
        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.paymentMethods = response.data.data.paymentMethods || [];
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error);
      }
    },

    // Fetch Hospital Collections
    async fetchHospitalCollections() {
      this.loading = true;
      this.error = null;

      try {
        const offset = (this.currentPage - 1) * this.perPage;

        const data = {
          function: "getHospitalCollections",
          startDate: this.startDate,
          endDate: this.endDate,
          regionId: this.filters.regionId,
          zoneId: this.filters.zoneId,
          woredaId: this.filters.woredaId,
          hospitalId: this.filters.hospitalId,
          searchTerm: this.searchTerm,
          limit: this.perPage,
          offset: offset
        };

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.hospitalCollections = response.data.data.collections || [];
          this.hospitalSummary = response.data.data.summary || null;
          this.pagination = response.data.data.pagination || this.pagination;
          this.updateDashboardStats();
        } else {
          this.error = response.data.message || 'Failed to fetch hospital collections';
        }
      } catch (error) {
        this.error = 'Failed to fetch hospital collections: ' + error.message;
        console.error('Error fetching hospital collections:', error);
      } finally {
        this.loading = false;
      }
    },

    // Fetch Agent Collections
    async fetchAgentCollections() {
      this.loading = true;
      this.error = null;

      try {
        const offset = (this.currentPage - 1) * this.perPage;

        const data = {
          function: "getAgentCollections",
          startDate: this.startDate,
          endDate: this.endDate,
          regionId: this.filters.regionId,
          zoneId: this.filters.zoneId,
          woredaId: this.filters.woredaId,
          searchTerm: this.searchTerm,
          limit: this.perPage,
          offset: offset
        };

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.agentCollections = response.data.data.collections || [];
          this.agentSummary = response.data.data.summary || null;
          this.pagination = response.data.data.pagination || this.pagination;
          this.updateDashboardStats();
        } else {
          this.error = response.data.message || 'Failed to fetch agent collections';
        }
      } catch (error) {
        this.error = 'Failed to fetch agent collections: ' + error.message;
        console.error('Error fetching agent collections:', error);
      } finally {
        this.loading = false;
      }
    },

    // Fetch Member Deposits
    async fetchMemberDeposits() {
      this.loading = true;
      this.error = null;

      try {
        const offset = (this.currentPage - 1) * this.perPage;

        const data = {
          function: "getMemberDeposits",
          startDate: this.startDate,
          endDate: this.endDate,
          regionId: this.filters.regionId,
          zoneId: this.filters.zoneId,
          woredaId: this.filters.woredaId,
          paymentMethodId: this.filters.paymentMethodId,
          searchTerm: this.searchTerm,
          limit: this.perPage,
          offset: offset
        };

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.memberDeposits = response.data.data.deposits || [];
          this.memberDepositsSummary = response.data.data.summary || null;
          this.pagination = response.data.data.pagination || this.pagination;
          this.updateDashboardStats();
        } else {
          this.error = response.data.message || 'Failed to fetch member deposits';
        }
      } catch (error) {
        this.error = 'Failed to fetch member deposits: ' + error.message;
        console.error('Error fetching member deposits:', error);
      } finally {
        this.loading = false;
      }
    },

    // Fetch Woreda Collections
    async fetchWoredaCollections() {
      this.loading = true;
      this.error = null;

      try {
        const offset = (this.currentPage - 1) * this.perPage;

        const data = {
          function: "getWoredaCollections",
          startDate: this.startDate,
          endDate: this.endDate,
          regionId: this.filters.regionId,
          zoneId: this.filters.zoneId,
          woredaId: this.filters.woredaId,
          searchTerm: this.searchTerm,
          limit: this.perPage,
          offset: offset
        };

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.woredaCollections = response.data.data.collections || [];
          this.woredaSummary = response.data.data.summary || null;
          this.pagination = response.data.data.pagination || this.pagination;
          this.updateDashboardStats();
        } else {
          this.error = response.data.message || 'Failed to fetch woreda collections';
        }
      } catch (error) {
        this.error = 'Failed to fetch woreda collections: ' + error.message;
        console.error('Error fetching woreda collections:', error);
      } finally {
        this.loading = false;
      }
    },

    // Fetch Channel Stats
    async fetchChannelStats() {
      this.loading = true;
      this.error = null;

      try {
        const data = {
          function: "getChannelStats",
          startDate: this.startDate,
          endDate: this.endDate,
          regionId: this.filters.regionId
        };

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.channelStats = response.data.data.channels || [];
          this.channelSummary = response.data.data.summary || [];
          this.updateDashboardStats();
        } else {
          this.error = response.data.message || 'Failed to fetch channel stats';
        }
      } catch (error) {
        this.error = 'Failed to fetch channel stats: ' + error.message;
        console.error('Error fetching channel stats:', error);
      } finally {
        this.loading = false;
      }
    },

    // ==================== HELPER METHODS ====================

    getChannelTotalTransactions() {
      return this.channelStats.reduce((sum, channel) => sum + parseInt(channel.transaction_count || 0), 0);
    },

    getChannelTotalMembers() {
      return this.channelStats.reduce((sum, channel) => sum + parseInt(channel.unique_members || 0), 0);
    },

    getChannelTotalAmount() {
      return this.channelStats.reduce((sum, channel) => sum + parseFloat(channel.total_amount || 0), 0);
    },

    getAgentSettledPercentage() {
      if (!this.agentSummary) return 0;
      const total = this.agentSummary.total_agents || 0;
      if (total === 0) return 0;
      const settled = this.agentSummary.settled_count || 0;
      return Math.round((settled / total) * 100);
    },

    updateDashboardStats() {
      let totalCollected = 0;
      let totalSettled = 0;
      let totalMembers = 0;
      let totalTransactions = 0;

      switch(this.activeTab) {
        case 'woredaCollections':
          if (this.woredaSummary) {
            totalCollected = this.woredaSummary.total_collected || 0;
            totalSettled = this.woredaSummary.total_settled || 0;
            totalMembers = this.woredaSummary.total_members || 0;
            totalTransactions = this.woredaSummary.total_transactions || 0;
          }
          break;
        case 'hospitals':
          if (this.hospitalSummary) {
            totalCollected = this.hospitalSummary.total_amount || 0;
            totalTransactions = this.hospitalSummary.total_transactions || 0;
          }
          break;
        case 'agents':
          if (this.agentSummary) {
            totalCollected = this.agentSummary.total_collected || 0;
            totalTransactions = this.agentSummary.total_transactions || 0;
            totalMembers = this.agentSummary.total_members || 0;
          }
          break;
        case 'memberDeposits':
          if (this.memberDepositsSummary) {
            totalCollected = this.memberDepositsSummary.total_amount || 0;
            totalMembers = this.memberDepositsSummary.total_deposits || 0;
          }
          break;
      }

      this.dashboardStats = {
        totalCollections: totalCollected,
        collectionChange: this.calculateCollectionChange(),
        settlementRate: totalCollected > 0 ? ((totalSettled / totalCollected) * 100).toFixed(1) : 0,
        settledAmount: totalSettled,
        collectionEfficiency: this.calculateEfficiency(),
        memberDeposits: totalTransactions || totalMembers,
        newDeposits: 0
      };
    },

    calculateCollectionChange() {
      return 12.5;
    },

    calculateEfficiency() {
      if (this.activeTab === 'woredaCollections' && this.woredaSummary) {
        const total = this.woredaSummary.total_collected || 0;
        const settled = this.woredaSummary.total_settled || 0;
        if (total > 0) {
          return ((settled / total) * 100).toFixed(1);
        }
      }
      return 100;
    },

    async downloadReport() {
      try {
        this.loading = true;

        const data = {
          function: "downloadPremiumReport",
          reportType: this.activeTab,
          startDate: this.startDate,
          endDate: this.endDate,
          format: 'csv',
          filters: this.filters,
          searchTerm: this.searchTerm
        };

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          const blob = new Blob([response.data.data.csv], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = response.data.data.filename || `${this.activeTab}_report_${this.startDate}_to_${this.endDate}.csv`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);

          this.showSuccess('Report downloaded successfully');
        } else {
          this.showError(response.data.message || 'Failed to download report');
        }
      } catch (error) {
        this.showError('Failed to download report: ' + error.message);
      } finally {
        this.loading = false;
      }
    },

    uploadBankStatement() {
      this.$emit('open-upload-modal');
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return '0.00';
      return Number(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatNumber(number) {
      if (!number) return '0';
      return Number(number).toLocaleString('en-US');
    },

    getFilterDescription() {
      const parts = [];

      if (this.filters.regionId) {
        const region = this.regions.find(r => r.region_id === this.filters.regionId);
        if (region) parts.push(`Region: ${region.region_name}`);
      }

      if (this.filters.zoneId) {
        const zone = this.zones.find(z => z.zone_id === this.filters.zoneId);
        if (zone) parts.push(`Zone: ${zone.zone_name}`);
      }

      if (this.filters.woredaId) {
        const woreda = this.woredas.find(w => w.woreda_id === this.filters.woredaId);
        if (woreda) parts.push(`Woreda: ${woreda.woreda_name}`);
      }

      if (this.filters.hospitalId) {
        const hospital = this.hospitals.find(h => h.hospital_id === this.filters.hospitalId);
        if (hospital) parts.push(`Facility: ${hospital.hospital_name}`);
      }

      if (this.searchTerm) {
        parts.push(`Search: "${this.searchTerm}"`);
      }

      return parts.length > 0 ? `Filtered by: ${parts.join(', ')}` : 'Showing all locations';
    },

    // Alert methods
    showSuccess(message) {
      this.clearAlerts();
      this.alert.success = true;
      this.message = message;
      setTimeout(() => this.clearAlerts(), 5000);
    },

    showError(message) {
      this.clearAlerts();
      this.alert.failed = true;
      this.message = message;
    },

    showInfo(message) {
      this.clearAlerts();
      this.alert.info = true;
      this.message = message;
      setTimeout(() => this.clearAlerts(), 5000);
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
      this.message = '';
    }
  }
};
</script>

<style scoped>
.stat-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.currency {
  font-size: 1.25rem;
  font-weight: 400;
}

.stat-change {
  font-size: 0.875rem;
}

.stat-change.positive {
  color: #10b759;
}

.stat-change.negative {
  color: #f74e4e;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-box {
  position: relative;
}

.search-box input {
  padding-right: 35px;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.875rem;
}

.text-right {
  text-align: right !important;
}

.text-center {
  text-align: center !important;
}

.badge {
  font-weight: 600;
  padding: 0.375rem 0.75rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.bg-purple {
  background-color: #6f42c1 !important;
}

.form-select-sm {
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 1rem;
}

.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}

.input-group-sm .input-group-text {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.input-group-sm .form-control {
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
}

.btn-group-sm .btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.btn-outline-primary:hover {
  background-color: #0d6efd;
  color: white;
}

.btn-outline-primary.active {
  background-color: #0d6efd;
  color: white;
}

/* Ethiopian/Gregorian Toggle Styles */
.btn-check:checked + .btn-outline-primary {
  background-color: #0d6efd;
  color: white;
}

.btn-check:checked + .btn-outline-success {
  background-color: #198754;
  color: white;
}

@media (max-width: 768px) {
  .col-md-2 {
    margin-bottom: 0.5rem;
  }

  .d-none.d-md-inline {
    display: none !important;
  }
}

select:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.btn-link.text-danger:hover {
  text-decoration: underline;
  opacity: 0.8;
}

.form-select:disabled {
  opacity: 0.6;
}

.alert {
  margin-top: 1rem;
  padding: 0.75rem 1.25rem;
  border-radius: 0.25rem;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.table-primary {
  background-color: #f0f8ff !important;
}

.display-1 {
  font-size: 5rem;
}

.display-6 {
  font-size: 2.5rem;
}

.text-muted {
  color: #6c757d !important;
}

/* Pagination styles */
.pagination {
  margin-bottom: 0;
}

.page-link {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  background-color: #fff;
  border-color: #dee2e6;
}

/* Refresh button spin animation */
.bx-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>